// #region imports and types
import { useEffect, useRef, useState, RefObject } from 'react';
import { SheetDefaults } from '../constants/sheetDefaults';
import { ReturnTypeofSheetSectionsSelector } from '../selectors/sheetUiSelector';
import { getFreezeBarHandleSize } from '../utils/viewport/viewportUtils';
import useDraggable from '../../core/hooks/useDraggable';
import { Freeze, GridEntity } from '../types';
import _, { debounce } from 'lodash';
import { useAppDispatch } from '../../core/hooks/storeHooks';
import { setViewportFromScrollbarPosition } from '../redux/sagas/views/viewsActions';
import {
  UseScrollbar,
  calculateThumbSize,
  getMaxAvailableCountForScroll,
  mapScrolledIndexToVisibleIndex,
} from './useScrollbar';
import { UseSheetTheme } from './useSheetTheme';
import { getViewportStartEnd } from '../utils/views/virtualViewport';
import { sortRanges } from '../utils/area/range';

export interface ColScrollbarProps
  extends ReturnTypeofSheetSectionsSelector,
    Omit<UseScrollbar, 'handleWheelScroll'>,
    UseSheetTheme {
  sheetId: string;
  gridWidth: number;
  windowWidth: number;
  windowHeight: number;
  windowX: number;
}
// #endregion

export default (props: ColScrollbarProps) => {
  // #region state and variables
  const dispatch = useAppDispatch();
  const thumbRef = useRef<HTMLDivElement>(null);

  const [isFreed, setIsFreed] = useState(true);
  const [thumbPosition, setThumbPosition] = useState(0);
  const [thumbWidth, setThumbWidth] = useState(200);

  const scrollGeometry = getScrollbarGeometry(props, thumbWidth);

  const {
    sheetId,
    windowWidth,
    windowHeight,
    colViewport,
    freezedCols,
    isRtl,
    maxColIndex,
  } = props;

  // Sorting is important to map virtualIndex to actualIndex
  const sortedHiddenCols = sortRanges(props.hiddenCols);
  const minIndex = freezedCols.frozen ? freezedCols.end + 1 : 0;
  const maxIndex = getMaxAvailableCountForScroll(
    maxColIndex,
    sortedHiddenCols,
    minIndex
  );
  // #endregion

  // #region useEffect
  useEffect(() => {
    if (isFreed) {
      const { visibleThumbMax } = scrollGeometry;
      const { start, end } = getViewportStartEnd(colViewport.nonFrozenIndexes);

      const virtualIndex = getMaxAvailableCountForScroll(
        start,
        sortedHiddenCols,
        minIndex
      );

      // TODO: Get un hidden max if end >= unHiddenMax (this is the actual index)
      const thumbPosition =
        end >= maxColIndex
          ? visibleThumbMax
          : (virtualIndex * visibleThumbMax) / maxIndex;

      setThumbPosition(thumbPosition);
      requestAnimationFrame(() =>
        updateThumbPositionThroughRef(thumbRef, thumbPosition, isRtl)
      );
    }
  }, [colViewport, isFreed, scrollGeometry.visibleThumbMax]);

  useEffect(() => {
    const minWidth = 20;
    const maxWidth = 200;

    const { scrollAreaWidth } = scrollGeometry;

    // console.log("colViewport")

    if (colViewport.nonFrozenIndexes.length - 1 === maxIndex) {
      setThumbWidth(0);
    } else {
      const thumbSize = calculateThumbSize(
        scrollAreaWidth,
        minWidth,
        maxIndex,
        maxWidth
      );

      setThumbWidth(Math.max(thumbSize, minWidth));
    }
  }, [scrollGeometry.scrollAreaWidth, maxIndex]);
  // #endregion

  // #region on thumb drag
  const dispatchChanges = debounce((virtualIndex) => {
    dispatch(
      setViewportFromScrollbarPosition({
        sheetId: sheetId,
        entityType: GridEntity.Column,
        expectedIndex: virtualIndex,
        windowHeight: windowHeight,
        windowWidth: windowWidth,
      })
    );
  }, 10);

  const onThumbDrag = (
    x: number,
    y: number,
    e: MouseEvent,
    dragEnd?: boolean
  ) => {
    const {
      visibleThumbMax,
      scrollThumbMinPos,
      scrollThumbMaxPos,
      scrollThumbRtlMin,
      scrollThumbRtlMax,
    } = scrollGeometry;

    const min = isRtl ? scrollThumbRtlMin : scrollThumbMinPos;
    const max = isRtl ? scrollThumbRtlMax : scrollThumbMaxPos;

    const newPos = _.clamp(x, min, max);
    const visiblePos = isRtl
      ? Math.abs(scrollThumbRtlMax - newPos)
      : newPos - scrollThumbMinPos;

    const virtualIndex = Math.round((maxIndex * visiblePos) / visibleThumbMax);
    const visibleIndex = mapScrolledIndexToVisibleIndex(
      virtualIndex,
      sortedHiddenCols,
      minIndex
    );

    requestAnimationFrame(() =>
      updateThumbPositionThroughRef(thumbRef, visiblePos, isRtl)
    );

    dispatchChanges(visibleIndex);

    if (dragEnd) {
      setTimeout(() => {
        setIsFreed(true);
      }, 13);
      setThumbPosition(visiblePos);
    }
  };

  const { onMouseDown } = useDraggable({
    x: isRtl
      ? scrollGeometry.scrollThumbRtlMax - thumbPosition
      : scrollGeometry.scrollThumbMinPos + thumbPosition,
    y: 0,
    onDrag: onThumbDrag,
  });

  const onThumbMouseDown = (e: React.MouseEvent) => {
    setIsFreed(false);
    onMouseDown(e);
  };
  // #endregion

  return {
    ...scrollGeometry,
    scrollThumbWidth: thumbWidth,
    thumbPosition,

    thumbRef,
    onThumbMouseDown,
  };
};

// #region helpers
export const updateThumbPositionThroughRef = (
  thumbRef: RefObject<HTMLDivElement>,
  visiblePos: number,
  isRtl?: boolean
) => {
  if (thumbRef.current) {
    thumbRef.current.style[isRtl ? 'right' : 'left'] = `${visiblePos}px`;
  }
};
const getScrollbarGeometry = (
  props: ColScrollbarProps,
  scrollThumbWidth: number
) => {
  const { gridWidth, windowX, freezedCols, freezedColsSum } = props;

  const scrollBarHeight = SheetDefaults.scrollBarHeight;
  const buttonSize = scrollBarHeight;
  const leftSectionWidth = Math.max(props.rowHeaderWidth, buttonSize * 2);
  const buttonGroupWidth = buttonSize * 2;

  const freezeBarHandleSize = getFreezeBarHandleSize(freezedCols, props.zoom);

  const frozenAreaWidth = freezedCols.frozen
    ? freezedColsSum + freezeBarHandleSize
    : 0;

  const scrollAreaWidth = Math.max(
    gridWidth - leftSectionWidth - buttonGroupWidth - frozenAreaWidth,
    0
  );

  const scrollThumbMinPos =
    windowX + props.rowGroupBarWidth + leftSectionWidth + frozenAreaWidth;

  const scrollThumbMaxPos =
    scrollThumbMinPos + scrollAreaWidth - scrollThumbWidth;

  const visibleThumbMax = scrollThumbMaxPos - scrollThumbMinPos;

  const scrollThumbRtlMax =
    props.windowX +
    props.windowWidth -
    props.rowGroupBarWidth -
    frozenAreaWidth -
    leftSectionWidth;

  const scrollThumbRtlMin =
    props.windowX +
    props.windowWidth -
    props.rowGroupBarWidth -
    gridWidth +
    buttonGroupWidth +
    scrollThumbWidth;

  return {
    scrollBarHeight,
    buttonSize,
    leftSectionWidth,
    buttonGroupWidth,
    frozenAreaWidth,
    scrollAreaWidth,
    scrollThumbMinPos,
    scrollThumbMaxPos,
    visibleThumbMax,

    scrollThumbRtlMin,
    scrollThumbRtlMax,
  };
};
// #endregion
